import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TreeItemLike } from 'app/shared/components/tree-list/tree-list.component';

import Layer from '@arcgis/core/layers/Layer';
import {
  debounceTime,
  first,
  merge,
  Observable,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import { EsriUtils, SublayerTypes } from 'app/shared/utils/esri.utils';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { TocLayerItem, TocService } from '../../../../toc.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import Symbol from '@arcgis/core/symbols/Symbol';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import {
  TocMenuItemLike,
  TocMenuItemsDefault,
} from 'app/shared/models/toc-config';

export interface LayerTreeItemLike extends TreeItemLike<LayerTreeItemLike> {
  iconSizeAuto: boolean;
  isGroup: boolean;
  layerItem?: TocLayerItem;
  layer?: __esri.Layer | SublayerTypes;
  layerVisible?: boolean;
  notInScale?: boolean;
  dispose(): void;
}

@Component({
  selector: 'app-layer-list',
  templateUrl: 'layer-list.component.html',
  styleUrls: ['layer-list.component.scss'],
})
export class LayerListComponent implements OnChanges, OnDestroy {
  @ViewChild('layerLegendTpl', { static: true })
  layerLegendTpl: TemplateRef<any>;

  @Input()
  menuItem: TocMenuItemLike;

  @Input()
  mapView: __esri.MapView;

  itemList: Array<TocLayerItem>;

  @Input()
  searchText: string;

  allowDeleteLayer = false;
  levelsNotAllowedToDelete: number[] = [1];

  items: LayerTreeItemLike[];

  private searchTextSubjectInternal = new ReplaySubject<string>(1);
  private mainSearchTextSubscription: Subscription;

  private layerChangesSubscriptionMap = new Map<
    __esri.Layer | SublayerTypes | TocLayerItem,
    Subscription
  >();

  private layerLoadSubscription: Subscription;

  loadingVisibleObservable: Observable<boolean>;

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    private appConfig: AppConfigService,
    private mainMapService: MainMapService,
    private tocService: TocService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchText) {
      this.searchTextSubjectInternal.next(this.searchText);
    }

    if (changes.menuItem) {
      this.allowDeleteLayer =
        this.menuItem?.name === TocMenuItemsDefault.AdditionalLayers;

      this.handleLayerItems();
    }
  }

  ngOnDestroy(): void {
    this.layerLoadSubscription?.unsubscribe();
    this.mainSearchTextSubscription?.unsubscribe();

    this.unsubscribeLayerItemChangesSubscription();

    this.loadingVisibleObservable = null;
  }

  onBtnGoToLayerClick(e, { layer }: LayerTreeItemLike) {
    e?.stopPropagation();

    this.layerLoadSubscription?.unsubscribe();

    if (!layer) {
      return;
    }

    this.layerLoadSubscription = EsriUtils.load(layer).subscribe(() => {
      const fullExtent = (layer as __esri.Layer).fullExtent;
      if (!fullExtent) {
        return;
      }

      this.mainMapService.goTo(fullExtent);
    });
  }

  onBtnDeleteLayerClickInternal(e, item: LayerTreeItemLike) {
    e?.stopPropagation();

    item.dispose();

    const index = this.items?.indexOf(item);
    if (index > -1) {
      this.items.splice(index, 1);
    }
  }

  isItemVisible(item: LayerTreeItemLike) {
    return !!item.layerVisible;
  }

  onVisibleChange(
    item: LayerTreeItemLike,
    visible: boolean,
    parentItem?: LayerTreeItemLike
  ) {
    if (visible && item.hasItems && !item.opened) {
      item.opened = true;
    }

    const setLayerVisible = (treeItem: LayerTreeItemLike, visible: boolean) => {
      this.tocService.setLayerVisible(
        treeItem.layerItem ?? treeItem.layer,
        visible
      );
    };

    const setVisibleRecursively = (
      visible: boolean,
      item: LayerTreeItemLike,
      items: LayerTreeItemLike[]
    ) => {
      if (items?.length) {
        for (const itemAux of items) {
          if (
            itemAux === item ||
            setVisibleRecursively(visible, item, itemAux.items) ||
            !item
          ) {
            if (itemAux.layerVisible !== visible) {
              setLayerVisible(itemAux, visible);
            }

            if (item) {
              return true;
            }
          }
        }
      }

      return false;
    };

    if (this.appConfig.siiaMode && item.hasItems) {
      setVisibleRecursively(visible, null, item.items);
    }

    if (!parentItem || !visible) {
      setLayerVisible(item, visible);
      return;
    }

    setVisibleRecursively(true, item, this.items);
  }

  hasLayerOpacity(item: LayerTreeItemLike) {
    if (typeof (item.layer as __esri.Layer)?.opacity !== 'number') {
      return false;
    }

    return (
      (item.layer as any)?.capabilities?.exportMap?.supportsModification !==
      false
    );
  }

  private handleLayerItems() {
    this.mainSearchTextSubscription?.unsubscribe();
    this.unsubscribeLayerItemChangesSubscription();

    this.loadingVisibleObservable = this.tocService.observeMenuIsLoading(
      this.menuItem
    );

    const layersOrItems = this.tocService.getLayerItemsByMenu(this.menuItem);

    this.items = this._handleLayerItems(layersOrItems?.slice()?.reverse());

    this.mainSearchTextSubscription = this.searchTextSubjectInternal.subscribe(
      () => {
        const firstitemVisible = this.items?.find((x) => x.visible);
        if (firstitemVisible) {
          firstitemVisible.opened = true;
        }
      }
    );
  }

  private _handleLayerItems(
    layersOrItems: Array<__esri.Layer | SublayerTypes | TocLayerItem>
  ): LayerTreeItemLike[] {
    if (Array.isArray(layersOrItems)) {
      return layersOrItems.map((layerOrItem) => {
        return this._parseLayerTreeItem(layerOrItem);
      });
    }

    return null;
  }

  private _parseLayerTreeItem(
    layerOrItem: TocLayerItem | __esri.Layer | SublayerTypes
  ): LayerTreeItemLike {
    if (!layerOrItem) {
      return null;
    }

    if (!(layerOrItem instanceof TocLayerItem)) {
      const item = this.tocService.findLayerItemByMenuAndLayer(
        this.menuItem,
        layerOrItem
      );
      if (item) {
        layerOrItem = item;
      }
    }

    const { layerLegendTpl, tocService, layerChangesSubscriptionMap } = this;

    const subscription = new Subscription();
    layerChangesSubscriptionMap.get(layerOrItem)?.unsubscribe();
    layerChangesSubscriptionMap.set(layerOrItem, subscription);

    const textGetter = () => {
      return layerOrItem.title;
    };

    const layerGetter = () => {
      if (layerOrItem instanceof TocLayerItem) {
        return layerOrItem.layer;
      }

      return layerOrItem;
    };

    let subItems: LayerTreeItemLike[];

    let visible = true;

    subscription.add(
      this.searchTextSubjectInternal.subscribe((searchText) => {
        visible = this.checkLayerText(textGetter(), searchText);
      })
    );

    let opened: boolean;
    let notInScale: boolean;
    let legendIconStr: string;
    let isLegendSimple: boolean;
    let iconSizeAuto: boolean;
    let legendSimpleSymbol: __esri.Symbol;

    const loadLegendByLayer = (layer: any) => {
      const layerRenderer = (layer as any)?.renderer as __esri.Renderer;
      isLegendSimple = layerRenderer?.type === 'simple';
      legendSimpleSymbol = (layerRenderer as any)?.symbol;

      iconSizeAuto = legendSimpleSymbol instanceof PictureMarkerSymbol;

      if (legendSimpleSymbol instanceof Symbol) {
        EsriUtils.renderSymbolPreviewImageUrl(legendSimpleSymbol).then(
          (icon) => {
            legendIconStr = icon;
          }
        );
      }
    };

    const observeSublayers = (layer: __esri.Layer | SublayerTypes) => {
      if (!layer) {
        return;
      }

      let collection = EsriUtils.getSublayersCollection(layer as any);
      const observeCollectionFn = () => {
        const handleAddedItems = (
          layers: Array<__esri.Layer | SublayerTypes>
        ) => {
          if (layers?.length) {
            const layerItemsAdded = this._handleLayerItems(layers.reverse());
            if (!subItems) {
              subItems = [];
            }

            subItems.unshift(...layerItemsAdded);
          }
        };

        handleAddedItems(collection.toArray());

        const subscriptionAux = EsriUtils.on(collection, 'change').subscribe(
          (
            changes: __esri.CollectionChangeEvent<__esri.Layer | SublayerTypes>
          ) => {
            if (changes.removed?.length) {
              for (const layerAux of changes.removed) {
                const index = subItems.findIndex((x) => x.layer === layerAux);
                if (index > -1) {
                  subItems.splice(index, 1);
                }
              }

              this.unsubscribeLayerItemChangesSubscription(changes.removed);
            }

            handleAddedItems(changes.added);
          }
        );

        subscription.add(subscriptionAux);
      };

      if (collection) {
        observeCollectionFn();
      } else if (EsriUtils.isLayerWithCollection(layer)) {
        subscription.add(
          merge(
            EsriUtils.watch(layer, 'sublayers'),
            EsriUtils.watch(layer, 'layers')
          )
            .pipe(first((e) => e.newValue))
            .subscribe(() => {
              collection = EsriUtils.getSublayersCollection(layer as any);
              observeCollectionFn();
            })
        );
      }
    };

    const observeScaleLayer = (layer: __esri.Layer | SublayerTypes) => {
      if ('minScale' in layer && 'maxScale' in layer) {
        const handleScaleItem = () => {
          const scale = this.mapView.scale;
          notInScale = !EsriUtils.isRecursiveLayerInScale(layer, scale);
        };

        handleScaleItem();
        subscription.add(
          EsriUtils.watch(this.mapView, 'scale')
            .pipe(debounceTime(100))
            .subscribe(() => {
              handleScaleItem();
            })
        );
      }
    };

    const setupLayer = (layer: __esri.Layer | SublayerTypes) => {
      if (!layer) {
        return;
      }

      layerChangesSubscriptionMap.set(layer, subscription);

      loadLegendByLayer(layer);
      observeSublayers(layer);
      observeScaleLayer(layer);
    };

    let disposed = false;
    const treeItem: LayerTreeItemLike = {
      get text() {
        return textGetter();
      },
      get layer() {
        return layerGetter();
      },
      layerItem: layerOrItem instanceof TocLayerItem ? layerOrItem : null,
      get iconSizeAuto() {
        return !!iconSizeAuto;
      },
      get icon() {
        return legendIconStr;
      },
      get hasItems() {
        return !!(this.isGroup || this.subListTemplate);
      },
      get items() {
        return subItems;
      },
      get isGroup() {
        return !!subItems;
      },
      get subListTemplate() {
        return this.isGroup ||
          legendSimpleSymbol ||
          !tocService.shouldShowLegend(layerGetter())
          ? null
          : layerLegendTpl;
      },
      get visible() {
        return !!(visible || subItems?.some((x) => x.visible));
      },
      get layerVisible() {
        return layerOrItem.visible;
      },
      set layerVisible(value) {
        layerOrItem.visible = !!value;
      },
      get opened() {
        return !!(opened ?? isLegendSimple);
      },
      set opened(value) {
        opened = value;
      },
      get notInScale() {
        return !!notInScale;
      },
      get canToggleOpened() {
        return !isLegendSimple;
      },
      get openedIndicatorEnabled() {
        return !isLegendSimple;
      },
      dispose() {
        if (disposed) {
          return;
        }

        disposed = true;

        subItems?.forEach((x) => {
          x?.dispose();
        });

        if (layerOrItem instanceof TocLayerItem) {
          layerOrItem.dispose();
        } else {
          EsriUtils.destroyLayersRecursive(layerOrItem);
        }

        subscription.unsubscribe();
      },
    };

    if (layerGetter()) {
      setupLayer(layerGetter());
    } else if (layerOrItem instanceof TocLayerItem) {
      subscription.add(
        layerOrItem.whenLayerLoaded.subscribe(() => {
          setupLayer(layerGetter());
        })
      );

      subscription.add(
        layerOrItem.onDispose.subscribe(() => {
          treeItem.dispose();
        })
      );
    }

    subscription.add(() => {
      if (subItems?.length) {
        this.unsubscribeLayerItemChangesSubscription(
          subItems.map((x) => {
            return x.layerItem ?? x.layer;
          })
        );
      }

      this.layerChangesSubscriptionMap.delete(layerOrItem);
      this.layerChangesSubscriptionMap.delete(layerGetter());
    });

    return treeItem;
  }

  private unsubscribeLayerItemChangesSubscription(
    layersOrItems?: (__esri.Layer | SublayerTypes | TocLayerItem)[]
  ) {
    if (!layersOrItems) {
      layersOrItems = Array.from(this.layerChangesSubscriptionMap.keys());
    } else {
      layersOrItems = layersOrItems?.slice();
    }

    if (!layersOrItems?.length) {
      return;
    }

    for (const x of layersOrItems) {
      this.layerChangesSubscriptionMap.get(x)?.unsubscribe();
    }
  }

  private checkLayerText(text: string, searchText: string): boolean {
    if (!searchText) {
      return true;
    }

    searchText = searchText.toLowerCase();
    text = text?.toLowerCase();
    return text?.includes(searchText);
  }
}
